import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { paths } from '@/app/routes';

export const PREVIEW_TRIGGER_SELECTOR = '[data-deal-preview-trigger]';
export const CLIENT_PREVIEW_TRIGGER_SELECTOR = '[data-client-preview-trigger]';
export const PREVIEW_IGNORE_CLOSE_DATA_ATTR = 'data-preview-ignore-close';
export const PREVIEW_IGNORE_CLOSE_SELECTOR = `[${PREVIEW_IGNORE_CLOSE_DATA_ATTR}]`;

export const isClientPreviewRoute = (pathname) =>
	pathname === paths.home || pathname === paths.prospects;

const OUTSIDE_CLOSE_IGNORE_SELECTOR = [
	PREVIEW_TRIGGER_SELECTOR,
	CLIENT_PREVIEW_TRIGGER_SELECTOR,
	PREVIEW_IGNORE_CLOSE_SELECTOR,
	'.MuiModal-root',
	'.MuiPopover-root',
	'.MuiMenu-root',
	'.MuiPopper-root',
	'.MuiPickersPopper-root',
].join(', ');

export const usePreviewMode = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { pathname } = useLocation();

	const previewId = searchParams.get('preview');

	const isPreviewMode = useMemo(() => !!previewId, [previewId]);
	const isClientPreviewRouteActive = useMemo(
		() => isClientPreviewRoute(pathname),
		[pathname],
	);
	const isClientPreviewMode = useMemo(
		() => isPreviewMode && isClientPreviewRouteActive,
		[isPreviewMode, isClientPreviewRouteActive],
	);
	const isDealPreviewMode = useMemo(
		() => isPreviewMode && !isClientPreviewRouteActive,
		[isPreviewMode, isClientPreviewRouteActive],
	);

	const closePreview = useCallback(() => {
		if (!searchParams.has('preview')) {
			return;
		}

		const next = new URLSearchParams(searchParams);
		next.delete('preview');
		setSearchParams(next, { replace: true });
	}, [searchParams, setSearchParams]);

	const openPreview = useCallback(
		(dealId) => {
			const next = new URLSearchParams(searchParams);
			next.set('preview', String(dealId));
			setSearchParams(next);
		},
		[searchParams, setSearchParams],
	);

	return {
		previewId,
		isPreviewMode,
		isClientPreviewMode,
		isDealPreviewMode,
		closePreview,
		openPreview,
	};
};

export const usePreviewClickOutside = (containerRef) => {
	const { isPreviewMode, closePreview } = usePreviewMode();

	useEffect(() => {
		if (!isPreviewMode) return;

		const handleMouseDown = (event) => {
			if (containerRef.current?.contains(event.target)) return;
			if (event.target.closest(OUTSIDE_CLOSE_IGNORE_SELECTOR)) return;
			closePreview();
		};

		document.addEventListener('mousedown', handleMouseDown);
		return () => document.removeEventListener('mousedown', handleMouseDown);
	}, [closePreview, containerRef, isPreviewMode]);
};
